import React, { FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useMountEffect } from '../../hooks/MountEffect'
import styles from './CustomModal.module.css'

export enum CustomModalWidth {
  w580 = '580px',
  w640 = '640px',
  w675 = '675px',
  w720 = '720px',
  w780 = '780px',
}

const widthClassMap: Record<CustomModalWidth, string> = {
  [CustomModalWidth.w580]: styles.width580px,
  [CustomModalWidth.w640]: styles.width640px,
  [CustomModalWidth.w675]: styles.width675px,
  [CustomModalWidth.w720]: styles.width720px,
  [CustomModalWidth.w780]: styles.width780px,
}

interface Props {
  id: string
  width?: CustomModalWidth
  close: () => void
  additionalClassName?: string
  parentClass?: string
  children?: ReactNode
}

const CustomModal: FunctionComponent<Props> = props => {
  const { close } = props
  const container = useRef(document.createElement('div', {}))
  if (props.parentClass) {
    container.current.classList.add(props.parentClass)
  }

  const onClose = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()
    props.close()
  }

  useMountEffect(() => {
    const current = container.current
    document.body.appendChild(current)
    return () => {
      document.body.removeChild(current)
    }
  })

  useEffect(() => {
    const escapeListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) close()
    }

    document.addEventListener('keyup', escapeListener)
    return () => {
      document.removeEventListener('keyup', escapeListener)
    }
  }, [close])

  const widthClass = props.width ? widthClassMap[props.width] ?? '' : ''

  return createPortal(
    <div
      onClick={e => {
        e.stopPropagation()
        props.close()
      }}
      className={`${styles.overlay} overlay`}
    >
      <div
        onClick={e => e.stopPropagation()}
        id={props.id}
        className={`popup ${styles.modal} ${props.additionalClassName ?? ''} ${widthClass}`}
      >
        <a href='/#' onClick={onClose} className='lines-button close' aria-label='Close'>
          <span className='lines' />
        </a>

        {props.children}
      </div>
    </div>,
    container.current
  )
}

export default CustomModal
